// Hero stage: tilts the hero media in 3D.
// - Desktop: follows the mouse across the hero.
// - Phones: follows the phone's angle. Android allows this straight away; iPhone
//   asks the visitor once, the first time they tap the picture. Dragging a finger
//   across the picture also tilts it on any phone, and a gentle float runs until
//   real tilt data arrives.
// Does nothing for visitors who prefer reduced motion.
const MAX_TILT = 8;

class SHeroStage extends HTMLElement {
  connectedCallback() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    this.frame = 0;
    this.onPointerMove = this.onPointerMove.bind(this);
    this.onReset = this.onReset.bind(this);
    this.onOrientation = this.onOrientation.bind(this);
    this.onFirstTap = this.onFirstTap.bind(this);

    if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
      this.area = this.closest('[aria-labelledby]') || this;
      this.area.addEventListener('pointermove', this.onPointerMove);
      this.area.addEventListener('pointerleave', this.onReset);
      return;
    }

    // Touch devices.
    this.setAttribute('data-float', '');
    this.style.touchAction = 'pan-y';
    this.area = this;
    this.area.addEventListener('pointermove', this.onPointerMove);
    this.area.addEventListener('pointerup', this.onReset);
    this.area.addEventListener('pointercancel', this.onReset);

    if (!('DeviceOrientationEvent' in window)) return;
    if (typeof DeviceOrientationEvent.requestPermission === 'function') {
      // iPhone: permission must be requested from a tap.
      this.addEventListener('click', this.onFirstTap, { once: true });
    } else {
      window.addEventListener('deviceorientation', this.onOrientation);
    }
  }

  disconnectedCallback() {
    this.area?.removeEventListener('pointermove', this.onPointerMove);
    this.area?.removeEventListener('pointerleave', this.onReset);
    this.area?.removeEventListener('pointerup', this.onReset);
    this.area?.removeEventListener('pointercancel', this.onReset);
    this.removeEventListener('click', this.onFirstTap);
    window.removeEventListener('deviceorientation', this.onOrientation);
    cancelAnimationFrame(this.frame);
  }

  async onFirstTap() {
    try {
      const state = await DeviceOrientationEvent.requestPermission();
      if (state === 'granted') window.addEventListener('deviceorientation', this.onOrientation);
    } catch {
      // Visitor declined or the browser refused: finger drag and float still work.
    }
  }

  apply(x, y) {
    // x and y range from -1 to 1.
    cancelAnimationFrame(this.frame);
    this.frame = requestAnimationFrame(() => {
      this.style.setProperty('--ry', `${(x * MAX_TILT).toFixed(2)}deg`);
      this.style.setProperty('--rx', `${(-y * MAX_TILT).toFixed(2)}deg`);
      this.style.setProperty('--gx', `${(50 + x * 40).toFixed(1)}%`);
      this.style.setProperty('--gy', `${(40 + y * 35).toFixed(1)}%`);
      this.setAttribute('data-tilting', '');
    });
  }

  onPointerMove(event) {
    const rect = this.getBoundingClientRect();
    const touch = event.pointerType !== 'mouse';
    const spanX = touch ? rect.width / 2 : window.innerWidth / 2;
    const spanY = touch ? rect.height / 2 : window.innerHeight / 2;
    const x = (event.clientX - (rect.left + rect.width / 2)) / spanX;
    const y = (event.clientY - (rect.top + rect.height / 2)) / spanY;
    if (touch) this.removeAttribute('data-float');
    this.apply(Math.max(-1, Math.min(1, x)), Math.max(-1, Math.min(1, y)));
  }

  onReset() {
    if (this.usingOrientation) return;
    cancelAnimationFrame(this.frame);
    this.style.setProperty('--rx', '0deg');
    this.style.setProperty('--ry', '0deg');
    this.removeAttribute('data-tilting');
  }

  onOrientation(event) {
    if (event.gamma == null || event.beta == null) return;
    // Real tilt data has arrived: stop the fallback float.
    this.usingOrientation = true;
    this.removeAttribute('data-float');
    const x = Math.max(-1, Math.min(1, event.gamma / 25));
    const y = Math.max(-1, Math.min(1, (event.beta - 45) / 25));
    this.apply(x, y);
  }
}

if (!customElements.get('s-hero-stage')) customElements.define('s-hero-stage', SHeroStage);
